import { useState, useEffect } from 'react';

import { ACCESSIBILITY_CLASSES, defaultAccessibilitySettings } from '@/components/layout/constants';

type AccessibilitySettings = typeof defaultAccessibilitySettings;

type _AccessibilitySettingsKey = keyof AccessibilitySettings;

export const useAccessibility = (): {
  accessibilitySettings: AccessibilitySettings;
  toggleAccessibilitySetting: (key: string) => void;
} => {
  const [accessibilitySettings, setAccessibilitySettings] = useState<AccessibilitySettings>(() => {
    try {
      if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
        return defaultAccessibilitySettings;
      }
      const savedSettings = localStorage.getItem('accessibilitySettings');
      return savedSettings
        ? (JSON.parse(savedSettings) as AccessibilitySettings)
        : defaultAccessibilitySettings;
    } catch {
      return defaultAccessibilitySettings;
    }
  });

  const toggleAccessibilitySetting = (key: string) => {
    setAccessibilitySettings((prev: AccessibilitySettings) => {
      const validKeys: (keyof AccessibilitySettings)[] = [
        'highContrast',
        'grayscale',
        'invertColors',
        'highlightLinks',
        'readableFont',
        'largeText',
        'lineHeight',
        'letterSpacing',
      ];

      if (!validKeys.includes(key as keyof AccessibilitySettings)) {
        return prev;
      }

      switch (key) {
        case 'highContrast':
          return { ...prev, highContrast: !prev.highContrast };
        case 'grayscale':
          return { ...prev, grayscale: !prev.grayscale };
        case 'invertColors':
          return { ...prev, invertColors: !prev.invertColors };
        case 'highlightLinks':
          return { ...prev, highlightLinks: !prev.highlightLinks };
        case 'readableFont':
          return { ...prev, readableFont: !prev.readableFont };
        case 'largeText':
          return { ...prev, largeText: (prev.largeText + 1) % 3 };
        case 'lineHeight':
          return { ...prev, lineHeight: (prev.lineHeight + 1) % 3 };
        case 'letterSpacing':
          return { ...prev, letterSpacing: (prev.letterSpacing + 1) % 3 };
        default:
          return prev;
      }
    });
  };

  // Clear all accessibility classes from document root
  const clearAccessibilityClasses = (root: HTMLElement) => {
    const allAccessibilityClasses = Object.values(ACCESSIBILITY_CLASSES).flat().filter(Boolean);
    for (const cls of allAccessibilityClasses) root.classList.remove(cls);
  };

  // Apply boolean accessibility settings
  const applyBooleanSettings = (root: HTMLElement, settings: AccessibilitySettings) => {
    const booleanMappings = [
      { setting: settings.highContrast, className: ACCESSIBILITY_CLASSES.highContrast },
      { setting: settings.grayscale, className: ACCESSIBILITY_CLASSES.grayscale },
      { setting: settings.invertColors, className: ACCESSIBILITY_CLASSES.invertColors },
      { setting: settings.highlightLinks, className: ACCESSIBILITY_CLASSES.highlightLinks },
      { setting: settings.readableFont, className: ACCESSIBILITY_CLASSES.readableFont },
    ];

    for (const { setting, className } of booleanMappings) {
      if (setting && typeof className === 'string') {
        root.classList.add(className);
      }
    }
  };

  // Apply numeric accessibility settings
  const applyNumericSettings = (root: HTMLElement, settings: AccessibilitySettings) => {
    if (settings.largeText > 0) {
      const classes = ACCESSIBILITY_CLASSES.largeText;
      if (Array.isArray(classes)) {
        const className = classes[settings.largeText];
        if (className) {
          root.classList.add(className);
        }
      }
    }
    if (settings.lineHeight > 0) {
      const classes = ACCESSIBILITY_CLASSES.lineHeight;
      if (Array.isArray(classes)) {
        const className = classes[settings.lineHeight];
        if (className) {
          root.classList.add(className);
        }
      }
    }
    if (settings.letterSpacing > 0) {
      const classes = ACCESSIBILITY_CLASSES.letterSpacing;
      if (Array.isArray(classes)) {
        const className = classes[settings.letterSpacing];
        if (className) {
          root.classList.add(className);
        }
      }
    }
  };

  // Save accessibility settings to localStorage
  const saveSettingsToLocalStorage = (settings: AccessibilitySettings) => {
    try {
      if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        localStorage.setItem('accessibilitySettings', JSON.stringify(settings));
      }
    } catch {
      // Failed to save accessibility settings
    }
  };

  // Apply accessibility styles
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;

    clearAccessibilityClasses(root);
    applyBooleanSettings(root, accessibilitySettings);
    applyNumericSettings(root, accessibilitySettings);
    saveSettingsToLocalStorage(accessibilitySettings);
  }, [accessibilitySettings]);

  return {
    accessibilitySettings,
    toggleAccessibilitySetting,
  };
};
